import {
  useEffect,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  formatIngredientQuantity,
  getIngredientCategory,
  getRecipeTotalMinutes,
  ingredientCategoryLabel,
  useDeleteRecipe,
  useRecipes,
  type Recipe,
  type RecipeIngredient,
} from '@/lib/nutrition';
import { ConfirmDialog } from '@/features/settings/ConfirmDialog';
import { GenerateRecipeForm } from './GenerateRecipeForm';

/**
 * Vista "Recetas" del módulo Nutrición.
 *
 * Decisiones:
 * - Grid de cards (title + kcal + tags). Click → modal con detalle completo.
 * - El modal de detalle se implementa con la misma técnica que `ConfirmDialog`
 *   (role="dialog" aria-modal, Escape cierra, click fuera cierra para el caso
 *   de no-destructivo). NO usamos `<dialog>` HTML5 por la misma razón:
 *   `HTMLDialogElement.showModal()` no existe en jsdom.
 * - Búsqueda: filtro client-side por título y tags. No paginamos en M2.5
 *   (se asume volumen bajo, decenas de recetas); si el backend devuelve
 *   centenares, refactorizar a búsqueda server-side.
 * - Crear receta: botón abre un modal con `<GenerateRecipeForm />` que invoca
 *   `useGenerateRecipe()` y al éxito invalida la query y cierra el modal.
 * - Borrar receta: `ConfirmDialog` reutilizado del módulo settings —
 *   destructivo, foco inicial en cancelar.
 */
export function RecipesView(): ReactElement {
  const query = useRecipes();
  const deleteMutation = useDeleteRecipe();
  // M2.34.A — Leemos `?recipeId=` del router para auto-abrir el modal de
  // detalle cuando el usuario llega desde "Ver receta →" del WeekMealPlanView.
  const router = useRouter();
  const search = router.state.location.search as
    | Record<string, unknown>
    | undefined;
  const requestedRecipeId = useMemo(() => {
    const raw = search?.recipeId;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    return null;
  }, [search]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  // Auto-abre el modal de detalle cuando la lista carga y la URL pide una receta concreta.
  // Solo lo hacemos UNA VEZ por valor de `requestedRecipeId` — si el usuario
  // cierra el modal, no lo reabrimos en cada render.
  const [openedForRequest, setOpenedForRequest] = useState<number | null>(null);
  useEffect(() => {
    if (requestedRecipeId === null) return;
    if (openedForRequest === requestedRecipeId) return;
    if (!query.data) return;
    const found = query.data.find((r) => r.id === requestedRecipeId);
    if (found) {
      setSelectedRecipe(found);
      setOpenedForRequest(requestedRecipeId);
    }
  }, [requestedRecipeId, query.data, openedForRequest]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return query.data ?? [];
    return (query.data ?? []).filter((r) => {
      if (r.title.toLowerCase().includes(term)) return true;
      if (r.tags && r.tags.toLowerCase().includes(term)) return true;
      return false;
    });
  }, [query.data, searchTerm]);

  function confirmDelete() {
    if (pendingDeleteId === null) return;
    deleteMutation.mutate(pendingDeleteId, {
      onSuccess: () => {
        // Si la receta abierta en detalle era la borrada, cerramos el modal.
        if (selectedRecipe?.id === pendingDeleteId) setSelectedRecipe(null);
        setPendingDeleteId(null);
      },
    });
  }

  return (
    <section aria-label="Recetas" className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="recipes-search" className="block text-xs font-medium text-slate-700">
            Buscar por título o etiqueta
          </label>
          <input
            id="recipes-search"
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Ej: avena, pre-entreno…"
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Crear receta nueva
        </button>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando recetas…
        </p>
      )}

      {query.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudieron cargar las recetas. {query.error?.message ?? ''}
        </p>
      )}

      {query.data && filtered.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-600">
          {searchTerm
            ? 'No hay recetas que coincidan con tu búsqueda.'
            : 'Aún no tienes recetas. Crea la primera con el botón superior.'}
        </p>
      )}

      {filtered.length > 0 && (
        <ul role="list" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((r) => (
            <RecipeCard
              key={r.id}
              recipe={r}
              onOpen={() => setSelectedRecipe(r)}
              onRequestDelete={() => setPendingDeleteId(r.id)}
            />
          ))}
        </ul>
      )}

      {selectedRecipe && (
        <RecipeDetailModal
          recipe={selectedRecipe}
          onClose={() => setSelectedRecipe(null)}
          onRequestDelete={() => setPendingDeleteId(selectedRecipe.id)}
        />
      )}

      {showCreate && (
        <CreateRecipeModal
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      )}

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title="Borrar receta"
        description="Esta acción no se puede deshacer. La receta dejará de estar disponible y, si alguna comida del plan la referenciaba, perderá el vínculo."
        confirmLabel={deleteMutation.isPending ? 'Borrando…' : 'Borrar receta'}
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recipe card
// ---------------------------------------------------------------------------

function RecipeCard({
  recipe,
  onOpen,
  onRequestDelete,
}: {
  recipe: Recipe;
  onOpen: () => void;
  onRequestDelete: () => void;
}): ReactElement {
  const tags = parseTags(recipe.tags);
  // M2.34.A — el backend M2.34 devuelve `category` como array; recetas pre-M2.34
  // tienen `category=null`. Lo fusionamos con tags para mostrarlo como chip.
  const categories = recipe.category ?? [];
  const totalMin = getRecipeTotalMinutes(recipe);
  return (
    <li className="flex flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={onOpen}
        className="text-left focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        aria-label={`Ver detalle de ${recipe.title}`}
      >
        <h3 className="text-base font-semibold text-slate-900 hover:text-teal-700">
          {recipe.title}
        </h3>
      </button>
      <p className="mt-1 text-xs text-slate-600">
        {recipe.servings} {recipe.servings === 1 ? 'ración' : 'raciones'}
        {totalMin !== null && totalMin > 0 && (
          <> · <span aria-hidden="true">⏱</span> {totalMin} min total</>
        )}
      </p>
      {recipe.kcal_per_serving !== null && recipe.kcal_per_serving !== undefined && (
        <p className="mt-1 text-sm font-medium text-slate-900">
          {recipe.kcal_per_serving} kcal/ración
        </p>
      )}
      {(tags.length > 0 || categories.length > 0) && (
        <ul className="mt-2 flex flex-wrap gap-1">
          {categories.map((c) => (
            <li
              key={`cat-${c}`}
              className="rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-800 ring-1 ring-teal-200"
            >
              {c}
            </li>
          ))}
          {tags.map((t) => (
            <li
              key={`tag-${t}`}
              className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200"
            >
              {t}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-2">
        <button
          type="button"
          onClick={onOpen}
          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          Ver
        </button>
        <button
          type="button"
          onClick={onRequestDelete}
          className="rounded-md border border-rose-200 bg-white px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-1"
        >
          Borrar
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Modal — detalle de receta
// ---------------------------------------------------------------------------

function RecipeDetailModal({
  recipe,
  onClose,
  onRequestDelete,
}: {
  recipe: Recipe;
  onClose: () => void;
  onRequestDelete: () => void;
}): ReactElement {
  const tags = parseTags(recipe.tags);
  const categories = recipe.category ?? [];
  // M2.34.A — Si las `instructions` empiezan por "1.", "2." (las generadas
  // automáticamente por el meal plan), las parseamos como pasos numerados.
  // Si no, mostramos el bloque entero como texto pre-formateado (compat con
  // recetas M2.5 generadas por POST /recipe).
  const steps = parseNumberedSteps(recipe.instructions);
  return (
    <Modal title={recipe.title} onClose={onClose}>
      <div className="space-y-4 text-sm text-slate-800">
        <p className="text-xs text-slate-600">
          {recipe.servings} {recipe.servings === 1 ? 'ración' : 'raciones'}
          {recipe.prep_min !== null && recipe.prep_min !== undefined && (
            <> · Prep {recipe.prep_min} min</>
          )}
          {recipe.cook_min !== null && recipe.cook_min !== undefined && (
            <> · Cocción {recipe.cook_min} min</>
          )}
        </p>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Macros por ración
          </h4>
          <div className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <Macro label="Kcal" value={recipe.kcal_per_serving} />
            <Macro label="Proteína" value={recipe.protein_g_per_serving} suffix="g" />
            <Macro label="Carbohidratos" value={recipe.carbs_g_per_serving} suffix="g" />
            <Macro label="Grasas" value={recipe.fat_g_per_serving} suffix="g" />
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Ingredientes
          </h4>
          {recipe.ingredients.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600">Sin ingredientes.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm">
              {recipe.ingredients.map((ing, idx) => (
                <IngredientLine key={idx} ing={ing} />
              ))}
            </ul>
          )}
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {steps !== null ? 'Pasos' : 'Instrucciones'}
          </h4>
          {steps !== null ? (
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-800">
              {steps.map((s, idx) => (
                <li key={idx}>{s}</li>
              ))}
            </ol>
          ) : (
            <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-slate-800">
              {recipe.instructions}
            </pre>
          )}
        </section>

        {(tags.length > 0 || categories.length > 0) && (
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Etiquetas
            </h4>
            <ul className="mt-2 flex flex-wrap gap-1">
              {categories.map((c) => (
                <li
                  key={`cat-${c}`}
                  className="rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-800 ring-1 ring-teal-200"
                >
                  {c}
                </li>
              ))}
              {tags.map((t) => (
                <li
                  key={`tag-${t}`}
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200"
                >
                  {t}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <footer className="mt-6 flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
        <button
          type="button"
          onClick={onRequestDelete}
          className="rounded-md border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-2"
        >
          Borrar receta
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Cerrar
        </button>
      </footer>
    </Modal>
  );
}

function Macro({
  label,
  value,
  suffix,
}: {
  label: string;
  value: number | null | undefined;
  suffix?: string;
}): ReactElement {
  return (
    <div className="rounded-md bg-slate-50 p-2">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900">
        {value !== null && value !== undefined ? `${value}${suffix ? ` ${suffix}` : ''}` : '—'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal — crear receta
// ---------------------------------------------------------------------------

function CreateRecipeModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): ReactElement {
  return (
    <Modal title="Crear receta nueva" onClose={onClose}>
      <GenerateRecipeForm onSuccess={onCreated} onCancel={onClose} />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal base (genérico, mismo patrón que ConfirmDialog)
// ---------------------------------------------------------------------------

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}): ReactElement {
  const titleId = useId();

  // Bloquear scroll del body mientras el modal está abierto.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-6"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        // Click fuera cierra (sólo cuando el target es el backdrop).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
      >
        <header className="mb-4 flex items-start justify-between gap-4 border-b border-slate-200 pb-3">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseTags(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

// ---------------------------------------------------------------------------
// M2.34.A — Ingredient line + steps parser
// ---------------------------------------------------------------------------

/**
 * Línea de ingrediente con badge de categoría cuando está disponible (M2.34.A).
 * Recetas legacy (M2.5) muestran "cantidad + unidad" sin badge.
 */
function IngredientLine({ ing }: { ing: RecipeIngredient }): ReactElement {
  const qtyLabel = formatIngredientQuantity(ing);
  const cat = getIngredientCategory(ing);
  return (
    <li className="flex flex-wrap items-baseline gap-2">
      <span className="font-medium">{ing.name}</span>
      {qtyLabel && <span className="text-slate-600">— {qtyLabel}</span>}
      {cat && (
        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-600 ring-1 ring-slate-200">
          {ingredientCategoryLabel(cat)}
        </span>
      )}
    </li>
  );
}

/**
 * Parsea `instructions` como pasos numerados si el texto empieza con "1." y
 * cada salto de línea introduce el siguiente número. Si no detectamos el
 * patrón, devolvemos null y el caller renderiza el texto plano.
 *
 * Regex: línea que empieza por dígitos + punto opcional + espacio. Tolera
 * múltiples líneas en blanco entre pasos.
 */
function parseNumberedSteps(instructions: string): string[] | null {
  const trimmed = instructions.trim();
  if (!/^\d+[.)]\s+/.test(trimmed)) return null;
  // Split por inicios de paso: capturamos cada bloque que arranca por dígito.
  const lines = trimmed
    .split(/\n+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const steps: string[] = [];
  for (const line of lines) {
    const m = /^\d+[.)]\s+(.*)$/.exec(line);
    if (m) {
      steps.push(m[1] ?? line);
    } else if (steps.length > 0) {
      // Línea de continuación del paso anterior — la concatenamos.
      steps[steps.length - 1] = `${steps[steps.length - 1]} ${line}`;
    } else {
      // Línea suelta sin numerar antes de cualquier paso: descartamos el parse.
      return null;
    }
  }
  return steps.length > 0 ? steps : null;
}
